import React, { useState } from 'react';
import { User, Bell, Lock, Globe, HelpCircle, LogOut, ChevronRight, Mail, Phone, MapPin, Briefcase, Camera, Shield, Eye, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDarkModeContext } from '../App';
import { DarkModeToggle } from '../components/DarkModeToggle';
interface SettingItemProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
}
function SettingItem({
  icon,
  title,
  subtitle,
  action,
  onClick,
  danger
}: SettingItemProps) {
  return <motion.button whileHover={{
    x: 4
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${danger ? 'hover:bg-red-50 dark:hover:bg-red-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`} data-id="element-197">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${danger ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`} data-id="element-198">
        {icon}
      </div>
      <div className="flex-1 text-left" data-id="element-199">
        <p className={`font-medium ${danger ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} data-id="element-200">
          {title}
        </p>
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-201">
            {subtitle}
          </p>}
      </div>
      {action || <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-202" />}
    </motion.button>;
}
export function SettingsPage() {
  const {
    isDark,
    toggle
  } = useDarkModeContext();
  const [notifications, setNotifications] = useState(true);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 transition-colors duration-200" data-id="element-203">
      {/* Header */}
      <motion.div initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.4
    }} className="bg-white dark:bg-gray-800 px-6 pt-12 pb-6" data-id="element-204">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-205">
          Settings
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-206">
          Manage your account and preferences
        </p>
      </motion.div>

      <div className="p-6 space-y-6" data-id="element-207">
        {/* Profile Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm" data-id="element-208">
          <div className="flex items-center gap-4 mb-6" data-id="element-209">
            <div className="relative" data-id="element-210">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00D084] to-[#00B074] flex items-center justify-center overflow-hidden" data-id="element-211">
                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Lauri" alt="Profile" className="w-full h-full object-cover" data-id="element-212" />
              </div>
              <motion.button whileHover={{
              scale: 1.1
            }} whileTap={{
              scale: 0.9
            }} className="absolute bottom-0 right-0 w-7 h-7 bg-[#00D084] rounded-full flex items-center justify-center text-white shadow-lg" data-id="element-213">
                <Camera className="w-4 h-4" data-id="element-214" />
              </motion.button>
            </div>
            <div className="flex-1" data-id="element-215">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-216">
                Lauri Kask
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-217">
                lauri.kask@email.com
              </p>
              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="mt-2 text-sm font-medium text-[#00D084] hover:underline" data-id="element-218">
                Edit Profile
              </motion.button>
            </div>
          </div>

          <div className="space-y-2" data-id="element-219">
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-220">
              <Mail className="w-4 h-4" data-id="element-221" />
              <span data-id="element-222">lauri.kask@email.com</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-223">
              <Phone className="w-4 h-4" data-id="element-224" />
              <span data-id="element-225">+372 5123 4567</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-226">
              <MapPin className="w-4 h-4" data-id="element-227" />
              <span data-id="element-228">Tallinn, Estonia</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-229">
              <Briefcase className="w-4 h-4" data-id="element-230" />
              <span data-id="element-231">Looking for casual work</span>
            </div>
          </div>
        </motion.section>

        {/* Appearance Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden" data-id="element-232">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700" data-id="element-233">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-234">
              Appearance
            </h3>
          </div>
          <div className="p-2" data-id="element-235">
            <SettingItem icon={<Moon className="w-5 h-5" data-id="element-237" />} title="Dark Mode" subtitle={isDark ? 'Enabled' : 'Disabled'} action={<DarkModeToggle isDark={isDark} onToggle={toggle} data-id="element-238" />} data-id="element-236" />
          </div>
        </motion.section>

        {/* Notifications Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden" data-id="element-239">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700" data-id="element-240">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-241">
              Notifications
            </h3>
          </div>
          <div className="p-2" data-id="element-242">
            <SettingItem icon={<Bell className="w-5 h-5" data-id="element-244" />} title="Push Notifications" subtitle="Get notified about new job matches" action={<motion.button whileTap={{
            scale: 0.9
          }} onClick={() => setNotifications(!notifications)} className={`relative w-12 h-6 rounded-full transition-colors ${notifications ? 'bg-[#00D084]' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-245">
                  <motion.div animate={{
              x: notifications ? 24 : 0
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md" data-id="element-246" />
                </motion.button>} data-id="element-243" />
            <SettingItem icon={<Mail className="w-5 h-5" data-id="element-248" />} title="Email Notifications" subtitle="Receive job alerts via email" data-id="element-247" />
          </div>
        </motion.section>

        {/* Account Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden" data-id="element-249">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700" data-id="element-250">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-251">Account</h3>
          </div>
          <div className="p-2" data-id="element-252">
            <SettingItem icon={<Lock className="w-5 h-5" data-id="element-254" />} title="Change Password" subtitle="Update your password" data-id="element-253" />
            <SettingItem icon={<Shield className="w-5 h-5" data-id="element-256" />} title="Privacy Settings" subtitle="Control your data and visibility" data-id="element-255" />
            <SettingItem icon={<Eye className="w-5 h-5" data-id="element-258" />} title="Profile Visibility" subtitle="Manage who can see your profile" data-id="element-257" />
          </div>
        </motion.section>

        {/* Support Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden" data-id="element-259">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700" data-id="element-260">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-261">Support</h3>
          </div>
          <div className="p-2" data-id="element-262">
            <SettingItem icon={<HelpCircle className="w-5 h-5" data-id="element-264" />} title="Help Center" subtitle="Get help and support" data-id="element-263" />
            <SettingItem icon={<Globe className="w-5 h-5" data-id="element-266" />} title="Language" subtitle="English" data-id="element-265" />
          </div>
        </motion.section>

        {/* Logout */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden" data-id="element-267">
          <div className="p-2" data-id="element-268">
            <SettingItem icon={<LogOut className="w-5 h-5" data-id="element-270" />} title="Log Out" subtitle="Sign out of your account" danger data-id="element-269" />
          </div>
        </motion.section>

        {/* App Version */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} className="text-center text-xs text-gray-400 dark:text-gray-500 py-4" data-id="element-271">
          DREDGET v1.0.0
        </motion.div>
      </div>
    </motion.div>;
}